import { createContext, FC, ReactNode, useContext } from "react"
import cn from "classnames"

import { Text } from "ui-kit/components-branded/Text"
import { Icon } from "components"

interface ContextValue {
    maxCount: number
    current: number
    onBackClick?: (this: void) => void
}

interface ContextHandlers {
    onBackClick?: () => void
}

interface Props extends ContextValue, ContextHandlers {
    children: ReactNode
}

const StepsCounterContext = createContext<[ContextValue, ContextHandlers] | null>(null)

function useStepsCounterContext() {
    const currentContext = useContext(StepsCounterContext)
    if (!currentContext) {
        throw new Error(
            "useStepsCounterContext has to be used within <StepsCounterContext.Provider>"
        )
    }
    return currentContext
}

function BackIcon({ className }: { className?: string }) {
    return (
        <Icon
            type="arrow-left-green"
            size={24}
            className={cn("steps-counter__chevron", className)}
        />
    )
}

function Counter({ className, text }: { className?: string; text?: string }) {
    const [{ current, maxCount }] = useStepsCounterContext()
    return (
        <Text isSecondary className={cn("steps-counter__text has-color-grey", className)}>
            {text ? text : `Шаг ${current}/${maxCount}`}
        </Text>
    )
}

const BackClickHandler: FC<{ children: ReactNode; className?: string }> = ({
    children,
    className,
}) => {
    const [, { onBackClick }] = useStepsCounterContext()
    return (
        <div onClick={onBackClick} className={cn("is-clickable is-unselectable", className)}>
            {children}
        </div>
    )
}

export function StepsCounter({ children, ...props }: Props) {
    return (
        <StepsCounterContext.Provider
            value={[
                { maxCount: props.maxCount, current: props.current },
                { onBackClick: props.onBackClick },
            ]}
        >
            {children}
        </StepsCounterContext.Provider>
    )
}

StepsCounter.BackIcon = BackIcon
StepsCounter.Counter = Counter
StepsCounter.BackClickHandler = BackClickHandler
